<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Send, Loader2 } from 'lucide-vue-next'

const props = defineProps({
  reportUuid: {
    type: String,
    required: true
  },
  reportVersion: {
    type: Number,
    required: true
  },
  sessionUuid: {
    type: String,
    required: true
  },
  organizationId: {
    type: Number,
    required: true
  },
  contentRef: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['version-created'])
const store = useStore()

const isVisible = ref(false)
const selectedText = ref('')
const position = ref({ top: 0, left: 0 })
const message = ref('')
const isLoading = ref(false)

const handleMouseUp = () => {
  const selection = window.getSelection()
  const text = selection?.toString().trim()

  if (text && text.length > 0) {
    selectedText.value = text
    const range = selection.getRangeAt(0)
    const rect = range.getBoundingClientRect()
    const containerRect = props.contentRef.getBoundingClientRect()

    // Position popover at the end of selection
    position.value = {
      top: rect.bottom - containerRect.top + 8,
      left: Math.min(rect.right - containerRect.left, containerRect.width - 220)
    }
    isVisible.value = true
  }
}

const close = () => {
  isVisible.value = false
  selectedText.value = ''
  message.value = ''
}

const handleClickOutside = (e) => {
  if (isVisible.value && !e.target.closest('.selection-popover')) {
    close()
  }
}

const handleKeyDown = (e) => {
  if (e.key === 'Escape') {
    close()
  }
}

const handleSend = async () => {
  if (!message.value.trim() || isLoading.value) return

  isLoading.value = true

  try {
    // Format the message with /report_edit command
    // Include the selected text and user's edit instruction
    const editContent = `/report_edit
Selected text: "${selectedText.value}"
Edit instruction: ${message.value.trim()}`

    // Send message with report context (including version being edited)
    await store.dispatch('chat/sendMessage', {
      orgId: props.organizationId,
      sessionCode: props.sessionUuid,
      content: editContent,
      role: 'user',
      reportUuid: props.reportUuid,
      reportVersion: props.reportVersion
    })

    emit('version-created')
    close()
  } catch (error) {
    console.error('Error sending edit request:', error)
  } finally {
    isLoading.value = false
  }
}

onMounted(() => {
  props.contentRef?.addEventListener('mouseup', handleMouseUp)
  document.addEventListener('mousedown', handleClickOutside)
  document.addEventListener('keydown', handleKeyDown)
})

onUnmounted(() => {
  props.contentRef?.removeEventListener('mouseup', handleMouseUp)
  document.removeEventListener('mousedown', handleClickOutside)
  document.removeEventListener('keydown', handleKeyDown)
})
</script>

<template>
  <div
    v-if="isVisible"
    class="selection-popover absolute z-50 bg-background rounded-lg shadow-lg border border-border p-2 flex items-center gap-2"
    :style="{ top: `${position.top}px`, left: `${position.left}px` }"
  >
    <Input
      v-model="message"
      placeholder="How should this be changed?"
      class="w-48 text-sm"
      :disabled="isLoading"
      @keydown.enter="handleSend"
    />
    <Button
      size="sm"
      class="px-2"
      :disabled="!message.trim() || isLoading"
      @click="handleSend"
    >
      <Loader2 v-if="isLoading" class="h-4 w-4 animate-spin" />
      <Send v-else class="h-4 w-4" />
    </Button>
  </div>
</template>
